import React, { useMemo, useState } from 'react';
import { Button, Card, Empty, Space, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import TaskCard from '../tasks/TaskCard';
import type { CompletionProjection, WorkspaceApplication, WorkspaceRoute, WorkspaceTask } from '../../store/api/workspaceApi';
import {
  formatWorkspaceTasksCount,
  workspaceApplicationNumberLines,
  workspaceClientApplicationLines,
} from '../workspace/WorkspaceTasks';
import {
  workspaceHighlightForApplication,
  workspaceHighlightForRoute,
  workspaceRowHighlightStyle,
} from '../workspace/workspaceListShared';
import { isFinanceWorkspaceTask } from '../../domain/workspaceTaskSemantics';

const { Text } = Typography;

function taskIsOverdue(task: WorkspaceTask): boolean {
  return String(task.status || '').toUpperCase() === 'OVERDUE';
}

function isRequiresClosure(projection: CompletionProjection | null | undefined): boolean {
  return Boolean(projection?.physicalComplete && !projection?.overallComplete);
}

function isAttentionNeeded(
  projection: CompletionProjection | null | undefined,
  overdue: boolean,
  taskCount: number
): boolean {
  if (overdue) return true;
  if (isRequiresClosure(projection)) return true;
  if (!projection) return taskCount > 0;
  if (!projection.operationalComplete || !projection.financialComplete || !projection.workflowComplete) return true;
  return taskCount > 0;
}

function completionPriorityRank(
  projection: CompletionProjection | null | undefined,
  overdue: boolean,
  taskCount: number
): number {
  if (overdue) return 1;
  if (isRequiresClosure(projection)) return 2;
  if (isAttentionNeeded(projection, overdue, taskCount)) return 3;
  if (projection?.overallComplete) return 5;
  return 4;
}

function firstInlineTaskForRoute(routeId: number, tasks: WorkspaceTask[]): WorkspaceTask | null {
  const candidates = tasks.filter((task) => task.routeId === routeId);
  if (!candidates.length) return null;
  const sorted = [...candidates].sort((a, b) => {
    const aOverdue = String(a.status || '').toUpperCase() === 'OVERDUE' ? 1 : 0;
    const bOverdue = String(b.status || '').toUpperCase() === 'OVERDUE' ? 1 : 0;
    if (aOverdue !== bOverdue) return bOverdue - aOverdue;
    const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.POSITIVE_INFINITY;
    const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;
    return a.id - b.id;
  });
  return sorted[0] ?? null;
}

function firstInlineTaskForApplication(applicationId: number, tasks: WorkspaceTask[]): WorkspaceTask | null {
  const candidates = tasks.filter((task) => task.applicationId === applicationId);
  if (!candidates.length) return null;
  const sorted = [...candidates].sort((a, b) => {
    const aOverdue = String(a.status || '').toUpperCase() === 'OVERDUE' ? 1 : 0;
    const bOverdue = String(b.status || '').toUpperCase() === 'OVERDUE' ? 1 : 0;
    if (aOverdue !== bOverdue) return bOverdue - aOverdue;
    const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.POSITIVE_INFINITY;
    const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;
    return a.id - b.id;
  });
  return sorted[0] ?? null;
}

function routeDirection(route: WorkspaceRoute): string {
  return (
    route.direction ||
    (route.loadingCity || route.unloadingCity
      ? `${route.loadingCity ?? '—'} → ${route.unloadingCity ?? '—'}`
      : '—')
  );
}

export type DashboardQueueMode = 'operations' | 'finance';

export interface DashboardCompactQueuesProps {
  mode: DashboardQueueMode;
  routes: WorkspaceRoute[];
  applications: WorkspaceApplication[];
  applicationContextById?: Record<number, { basisDate?: string | null }>;
  routeTaskCounts: Record<number, number>;
  applicationTaskCounts: Record<number, number>;
  allTasks: WorkspaceTask[];
  onInlineTaskAction?: (task: WorkspaceTask) => void;
  maxRows?: number;
  /** Роль-зависимые ссылки на карточку (по умолчанию без query). */
  buildRouteHref?: (routeId: number) => string;
  buildApplicationHref?: (applicationId: number) => string;
  /** В режиме finance — открыть компактный финансовый контекст вместо перехода на полную карточку. */
  onOpenFinanceContext?: (p: { kind: 'route' | 'application'; id: number }) => void;
}

export const DashboardCompactQueues: React.FC<DashboardCompactQueuesProps> = ({
  mode,
  routes,
  applications,
  applicationContextById,
  routeTaskCounts,
  applicationTaskCounts,
  allTasks,
  onInlineTaskAction,
  maxRows = 12,
  buildRouteHref,
  buildApplicationHref,
  onOpenFinanceContext,
}) => {
  const navigate = useNavigate();
  const hrefRoute = buildRouteHref ?? ((id: number) => `/routes/${id}`);
  const hrefApplication = buildApplicationHref ?? ((id: number) => `/applications/${id}`);

  const openRouteEntity = (routeId: number) => {
    if (mode === 'finance' && onOpenFinanceContext) onOpenFinanceContext({ kind: 'route', id: routeId });
    else navigate(hrefRoute(routeId));
  };

  const openApplicationEntity = (applicationId: number) => {
    if (mode === 'finance' && onOpenFinanceContext) onOpenFinanceContext({ kind: 'application', id: applicationId });
    else navigate(hrefApplication(applicationId));
  };
  const [expandedRoutes, setExpandedRoutes] = useState<Record<number, boolean>>({});
  const [expandedApps, setExpandedApps] = useState<Record<number, boolean>>({});
  const taskPresentationContext = useMemo(() => ({ applications }), [applications]);

  const sortedRoutes = useMemo(() => {
    const list = [...routes];
    list.sort((a, b) => {
      const aOverdue = allTasks.some((t) => t.routeId === a.id && taskIsOverdue(t));
      const bOverdue = allTasks.some((t) => t.routeId === b.id && taskIsOverdue(t));
      const pa = completionPriorityRank((a as any).completionProjection, aOverdue, routeTaskCounts[a.id] ?? 0);
      const pb = completionPriorityRank((b as any).completionProjection, bOverdue, routeTaskCounts[b.id] ?? 0);
      if (pa !== pb) return pa - pb;
      return b.id - a.id;
    });
    return list.slice(0, maxRows);
  }, [routes, allTasks, routeTaskCounts, maxRows]);

  /** Маршруты с хотя бы одной финансовой задачей (часто routeId без applicationId — иначе очередь пустая). */
  const sortedFinanceRoutes = useMemo(() => {
    if (mode !== 'finance') return [];
    const list = routes.filter((route) =>
      allTasks.some((t) => t.routeId === route.id && isFinanceWorkspaceTask(t))
    );
    list.sort((a, b) => {
      const finA = allTasks.filter((t) => t.routeId === a.id && isFinanceWorkspaceTask(t));
      const finB = allTasks.filter((t) => t.routeId === b.id && isFinanceWorkspaceTask(t));
      const aOverdue = finA.some((t) => taskIsOverdue(t));
      const bOverdue = finB.some((t) => taskIsOverdue(t));
      const pa = completionPriorityRank((a as any).completionProjection, aOverdue, finA.length);
      const pb = completionPriorityRank((b as any).completionProjection, bOverdue, finB.length);
      if (pa !== pb) return pa - pb;
      return b.id - a.id;
    });
    return list.slice(0, maxRows);
  }, [mode, routes, allTasks, maxRows]);

  const sortedApplications = useMemo(() => {
    let list = [...applications];
    if (mode === 'finance') {
      const appIds = new Set<number>();
      allTasks.forEach((t) => {
        if (!isFinanceWorkspaceTask(t)) return;
        if (t.applicationId != null) appIds.add(t.applicationId);
      });
      list = list.filter((a) => appIds.has(a.id));
    }
    list.sort((a, b) => {
      const aOverdue = allTasks.some((t) => t.applicationId === a.id && taskIsOverdue(t));
      const bOverdue = allTasks.some((t) => t.applicationId === b.id && taskIsOverdue(t));
      const pa = completionPriorityRank((a as any).completionProjection, aOverdue, applicationTaskCounts[a.id] ?? 0);
      const pb = completionPriorityRank((b as any).completionProjection, bOverdue, applicationTaskCounts[b.id] ?? 0);
      if (pa !== pb) return pa - pb;
      return b.id - a.id;
    });
    return list.slice(0, maxRows);
  }, [applications, allTasks, applicationTaskCounts, maxRows, mode]);

  const showRoutes = mode === 'operations' && sortedRoutes.length > 0;
  const showFinanceRoutes = mode === 'finance' && sortedFinanceRoutes.length > 0;
  const showApps = sortedApplications.length > 0;

  if (!showRoutes && !showFinanceRoutes && !showApps) {
    return (
      <Card size="small" title={mode === 'finance' ? 'Финансовые задачи' : 'Очереди'}>
        <Empty description="Нет элементов для отображения" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }

  const routeTitle = 'Маршруты';
  const financeRouteTitle = 'Маршруты (финансы)';
  const appTitle = 'Заявки';

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {mode === 'finance' && (showFinanceRoutes || showApps) ? (
        <div>
          <Text strong style={{ fontSize: 15 }}>
            Финансовые задачи
          </Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
            Подтверждения, счета, оплаты и документы — то же выполнение, что в рабочем пространстве
          </Text>
        </div>
      ) : null}
      {showRoutes ? (
        <Card size="small" title={routeTitle} styles={{ body: { paddingTop: 8 } }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {sortedRoutes.map((route) => {
              const tasks = allTasks.filter((t) => t.routeId === route.id);
              const n = routeTaskCounts[route.id] ?? tasks.length;
              const highlight = workspaceHighlightForRoute(route.id, allTasks);
              const rowStyle = workspaceRowHighlightStyle(highlight.overdue, highlight.today);
              const expanded = !!expandedRoutes[route.id];
              const label = route.number?.trim() ? route.number : `№${route.id}`;
              const direction = routeDirection(route);
              const overdueLabel = highlight.overdue ? 'Просрочка' : null;

              return (
                <div
                  key={route.id}
                  style={{
                    ...rowStyle,
                    borderRadius: 8,
                    padding: '8px 10px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8,
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    role="presentation"
                    onClick={() => openRouteEntity(route.id)}
                  >
                    <Space wrap size={6}>
                      <Text strong>{label}</Text>
                      <Text type="secondary">{direction}</Text>
                      {overdueLabel ? <Tag color="red">{overdueLabel}</Tag> : null}
                      <Text type="secondary">{formatWorkspaceTasksCount(n)}</Text>
                    </Space>
                    <Space wrap onClick={(e) => e.stopPropagation()}>
                      {n > 0 && onInlineTaskAction && (
                        <Button
                          size="small"
                          type="link"
                          style={{ paddingInline: 0 }}
                          onClick={() => {
                            const t = firstInlineTaskForRoute(route.id, allTasks);
                            if (t) onInlineTaskAction(t);
                          }}
                        >
                          Выполнить
                        </Button>
                      )}
                      <Button
                        size="small"
                        type="default"
                        onClick={() => setExpandedRoutes((p) => ({ ...p, [route.id]: !expanded }))}
                      >
                        {expanded ? 'Скрыть задачи' : 'Показать задачи'}
                      </Button>
                    </Space>
                  </div>
                  {expanded && (
                    <div style={{ marginTop: 8, paddingLeft: 4, borderLeft: '2px solid #f0f0f0' }}>
                      {tasks.length === 0 ? (
                        <Text type="secondary">Нет активных задач</Text>
                      ) : (
                        tasks.map((task) => (
                          <TaskCard
                            key={task.id}
                            workspaceTask={task}
                            presentationContext={taskPresentationContext}
                            id={task.id}
                            taskType={task.taskType}
                            status={task.status}
                            compact
                            dueDate={task.dueDate}
                            routeId={task.routeId}
                            applicationId={task.applicationId}
                            entityType={task.entityType}
                            invoiceId={task.invoiceId}
                            obligationId={task.obligationId}
                            obligationStage={task.obligationStage}
                            routeStatus={task.routeStatus}
                            loadingCity={task.loadingCity}
                            unloadingCity={task.unloadingCity}
                            carrierName={task.carrierName}
                            onAction={onInlineTaskAction}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </Space>
        </Card>
      ) : null}

      {showFinanceRoutes ? (
        <Card size="small" title={financeRouteTitle} styles={{ body: { paddingTop: 8 } }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {sortedFinanceRoutes.map((route) => {
              const tasks = allTasks.filter((t) => t.routeId === route.id && isFinanceWorkspaceTask(t));
              const n = tasks.length;
              const highlight = workspaceHighlightForRoute(route.id, allTasks);
              const rowStyle = workspaceRowHighlightStyle(highlight.overdue, highlight.today);
              const expanded = !!expandedRoutes[route.id];
              const label = route.number?.trim() ? route.number : `№${route.id}`;
              const direction = routeDirection(route);
              const overdueLabel = tasks.some((t) => taskIsOverdue(t)) ? 'Просрочка' : null;

              return (
                <div
                  key={`fin-${route.id}`}
                  style={{
                    ...rowStyle,
                    borderRadius: 8,
                    padding: '8px 10px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8,
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    role="presentation"
                    onClick={() => openRouteEntity(route.id)}
                  >
                    <Space wrap size={6}>
                      <Text strong>{label}</Text>
                      <Text type="secondary">{direction}</Text>
                      {overdueLabel ? <Tag color="red">{overdueLabel}</Tag> : null}
                      <Text type="secondary">{formatWorkspaceTasksCount(n)}</Text>
                    </Space>
                    <Space wrap onClick={(e) => e.stopPropagation()}>
                      {n > 0 && onInlineTaskAction && (
                        <Button
                          size="small"
                          type="link"
                          style={{ paddingInline: 0 }}
                          onClick={() => {
                            const pool = allTasks.filter((x) => x.routeId === route.id && isFinanceWorkspaceTask(x));
                            const t = firstInlineTaskForRoute(route.id, pool.length ? pool : allTasks.filter((x) => x.routeId === route.id));
                            if (t) onInlineTaskAction(t);
                          }}
                        >
                          Выполнить
                        </Button>
                      )}
                      <Button
                        size="small"
                        type="default"
                        onClick={() => setExpandedRoutes((p) => ({ ...p, [route.id]: !expanded }))}
                      >
                        {expanded ? 'Скрыть задачи' : 'Показать задачи'}
                      </Button>
                    </Space>
                  </div>
                  {expanded && (
                    <div style={{ marginTop: 8, paddingLeft: 4, borderLeft: '2px solid #f0f0f0' }}>
                      {tasks.length === 0 ? (
                        <Text type="secondary">Нет финансовых задач</Text>
                      ) : (
                        tasks.map((task) => (
                          <TaskCard
                            key={task.id}
                            workspaceTask={task}
                            presentationContext={taskPresentationContext}
                            id={task.id}
                            taskType={task.taskType}
                            status={task.status}
                            compact
                            dueDate={task.dueDate}
                            routeId={task.routeId}
                            applicationId={task.applicationId}
                            entityType={task.entityType}
                            invoiceId={task.invoiceId}
                            obligationId={task.obligationId}
                            obligationStage={task.obligationStage}
                            routeStatus={task.routeStatus}
                            loadingCity={task.loadingCity}
                            unloadingCity={task.unloadingCity}
                            carrierName={task.carrierName}
                            onAction={onInlineTaskAction}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </Space>
        </Card>
      ) : null}

      {showApps ? (
        <Card size="small" title={appTitle} styles={{ body: { paddingTop: 8 } }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {sortedApplications.map((app) => {
              let tasks = allTasks.filter((t) => t.applicationId === app.id);
              if (mode === 'finance') {
                tasks = tasks.filter(isFinanceWorkspaceTask);
              }
              const n = mode === 'finance' ? tasks.length : applicationTaskCounts[app.id] ?? tasks.length;
              const highlight = workspaceHighlightForApplication(app.id, allTasks);
              const rowStyle = workspaceRowHighlightStyle(highlight.overdue, highlight.today);
              const expanded = !!expandedApps[app.id];
              const internalRaw =
                (app.internalNumber && String(app.internalNumber).trim()) ||
                (app.number && String(app.number).trim()) ||
                String(app.id);
              const internalLines = workspaceApplicationNumberLines(internalRaw, app.applicationDate ?? null);
              const basis = applicationContextById?.[app.id]?.basisDate;
              const clientLines = workspaceClientApplicationLines(app.externalNumber ?? null, basis ?? null);
              const direction = app.direction || '—';
              const overdueLabel = highlight.overdue ? 'Просрочка' : null;

              return (
                <div
                  key={app.id}
                  style={{
                    ...rowStyle,
                    borderRadius: 8,
                    padding: '8px 10px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8,
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    role="presentation"
                    onClick={() => openApplicationEntity(app.id)}
                  >
                    <Space wrap size={6} align="start">
                      <div style={{ minWidth: 0 }}>
                        <Text strong>{internalLines.line1}</Text>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {direction}
                        </Text>
                      </div>
                      {overdueLabel ? <Tag color="red">{overdueLabel}</Tag> : null}
                      <Text type="secondary">{formatWorkspaceTasksCount(n)}</Text>
                    </Space>
                    <Space wrap onClick={(e) => e.stopPropagation()}>
                      {n > 0 && onInlineTaskAction && (
                        <Button
                          size="small"
                          type="link"
                          style={{ paddingInline: 0 }}
                          onClick={() => {
                            const forApp = allTasks.filter((x) => x.applicationId === app.id);
                            const pool =
                              mode === 'finance'
                                ? forApp.filter(isFinanceWorkspaceTask)
                                : forApp;
                            const pick = firstInlineTaskForApplication(app.id, pool.length ? pool : forApp);
                            if (pick) onInlineTaskAction(pick);
                          }}
                        >
                          Выполнить
                        </Button>
                      )}
                      <Button
                        size="small"
                        type="default"
                        onClick={() => setExpandedApps((p) => ({ ...p, [app.id]: !expanded }))}
                      >
                        {expanded ? 'Скрыть задачи' : 'Показать задачи'}
                      </Button>
                    </Space>
                  </div>
                  {expanded && (
                    <div style={{ marginTop: 8, paddingLeft: 4, borderLeft: '2px solid #f0f0f0' }}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                        {clientLines.line1} · {clientLines.line2}
                      </Text>
                      {tasks.length === 0 ? (
                        <Text type="secondary">Нет задач</Text>
                      ) : (
                        tasks.map((task) => (
                          <TaskCard
                            key={task.id}
                            workspaceTask={task}
                            presentationContext={taskPresentationContext}
                            id={task.id}
                            taskType={task.taskType}
                            status={task.status}
                            compact
                            dueDate={task.dueDate}
                            routeId={task.routeId}
                            applicationId={task.applicationId}
                            entityType={task.entityType}
                            invoiceId={task.invoiceId}
                            obligationId={task.obligationId}
                            obligationStage={task.obligationStage}
                            routeStatus={task.routeStatus}
                            loadingCity={task.loadingCity}
                            unloadingCity={task.unloadingCity}
                            carrierName={task.carrierName}
                            onAction={onInlineTaskAction}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </Space>
        </Card>
      ) : null}
    </Space>
  );
};
